import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, ReferenceLine, ReferenceArea, Cell, Tooltip,
} from 'recharts';
import { getGPS, getPlayers } from '../../api';
import { useSeason, dateToSeason } from '../../SeasonContext';
import { useUrlParam } from '../../hooks/useUrlParam';

const LAMBDA_ACUTE    = 2 / 8;    // 0.25  — matches Power BI
const LAMBDA_CHRONIC  = 2 / 29;   // ≈0.069 — matches Power BI
const HORIZON_ACUTE   = 60;       // calendar-day lookback for acute
const HORIZON_CHRONIC = 120;      // calendar-day lookback for chronic
const RANGE_OPTIONS   = [30, 60, 80, 90];

// Add y/m/d ints to a local Date without UTC offset issues
function addDays(dateStr, delta) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
function localToday() {
  const dt = new Date();
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

// Power BI weighted-sum approach: iterate every calendar day in the horizon
function calcEWMA(sessionMap, maxDateStr, lambda, horizonDays) {
  let total = 0;
  for (let daysAgo = 0; daysAgo <= horizonDays; daysAgo++) {
    const dateStr = addDays(maxDateStr, -daysAgo);
    const load    = sessionMap.get(dateStr) || 0;
    total += lambda * Math.pow(1 - lambda, daysAgo) * load;
  }
  return total;
}

function riskInfo(acwr) {
  if (acwr == null) return { label: '—',          color: 'var(--text-muted)' };
  if (acwr > 1.5)   return { label: 'High Risk',   color: '#ef4444' };
  if (acwr >= 1.3)  return { label: 'Caution',     color: '#f59e0b' };
  if (acwr >= 0.8)  return { label: 'Optimal',     color: '#10b981' };
  return             { label: 'Underload',         color: '#94a3b8' };
}

function dotColor(acwr) {
  if (acwr == null)  return '#3b82f6';
  if (acwr > 1.5)    return '#ef4444';
  if (acwr >= 1.3)   return '#f59e0b';
  if (acwr >= 0.8)   return '#10b981';
  return '#94a3b8';
}

function fmtDate(d) {
  if (!d) return '';
  const parts = d.split('-');
  return `${parts[2]}/${parts[1]}`;
}

function ACWRDot(props) {
  const { cx, cy, payload } = props;
  if (!payload || payload.acwr == null || cx == null || cy == null) return null;
  const ooz = payload.acwr > 1.5 || payload.acwr < 0.8;
  const col  = dotColor(payload.acwr);
  if (!ooz) {
    return <circle cx={cx} cy={cy} r={2.5} fill={col} />;
  }
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill={col} stroke="var(--bg-card)" strokeWidth={1.5} />
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize={9} fill={col} fontWeight="700">
        {payload.acwr.toFixed(2)}
      </text>
    </g>
  );
}

function ACWRTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d    = payload[0]?.payload;
  if (!d) return null;
  const zone = riskInfo(d.acwr);
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, minWidth: 160 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 5 }}>{d.date}</div>
      {d.title && <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 6 }}>{d.title}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ color: 'var(--text-secondary)' }}>
          ACWR: <strong style={{ color: zone.color }}>{d.acwr?.toFixed(3) ?? '—'}</strong>
        </span>
        <span style={{ color: 'var(--text-secondary)' }}>
          Zone: <span style={{ color: zone.color }}>{zone.label}</span>
        </span>
        <span style={{ color: 'var(--text-secondary)' }}>
          Acute: <strong style={{ color: 'var(--text-primary)' }}>{Math.round(d.acute ?? 0).toLocaleString()}</strong>
        </span>
        <span style={{ color: 'var(--text-secondary)' }}>
          Chronic: <strong style={{ color: 'var(--text-primary)' }}>{Math.round(d.chronic ?? 0).toLocaleString()}</strong>
        </span>
      </div>
    </div>
  );
}

function HMLDTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 5 }}>{d.date}</div>
      {d.title && <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 5 }}>{d.title}</div>}
      <span style={{ color: 'var(--text-secondary)' }}>
        HMLD: <strong style={{ color: 'var(--text-primary)' }}>{Math.round(d.hmld).toLocaleString()} m</strong>
      </span>
      <div style={{ color: 'var(--text-dimmed)', fontSize: 11, marginTop: 3 }}>{d.isMatch ? 'Match' : 'Training'}</div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function EWMA() {
  const { selectedSeasons } = useSeason();
  const [players, setPlayers]     = useState([]);
  const [sessions, setSessions]   = useState([]);
  const [playerId, setPlayerId]   = useUrlParam('player_id', '');
  const [rangeDaysRaw, setRangeDays] = useUrlParam('range_days', '60');
  const rangeDays = Number(rangeDaysRaw) || 60;
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([getGPS(), getPlayers()])
      .then(([gps, pl]) => {
        setSessions(gps || []);
        const list = (pl || []);
        setPlayers(list);
        const active = list.filter(p => p.active !== false);
        // Bug fix (2026-09-03): used to default to the first active player UNCONDITIONALLY,
        // discarding a `player_id` already in the URL — see PlayerChronic.js's identical fix
        // (window.location.search read directly, not the closure-captured useUrlParam binding —
        // that alone still showed the wrong player live; see that file's comment for why).
        const urlPlayerId = new URLSearchParams(window.location.search).get('player_id');
        if (!urlPlayerId && active.length) setPlayerId(String(active[0].id));
      })
      .finally(() => setLoading(false));
  }, []);

  // All sessions for this player with HMLD > 0, sorted ascending. is_estimated rows are included
  // — GPS session estimation's metrics split by validated accuracy, not by scoring component
  // (settled 2026-08-22): TD and HMLD (MdAPE 10.07%, vol bias -0.34%/-0.22%) are usable anywhere
  // in the physical pillar, RD/SA/HID are display-only everywhere, estimated or not. This page
  // only ever reads hmld (there's no "cumulative distance" concept here, just the ACWR ratio),
  // so TD's approval doesn't change anything in this specific file — matching
  // pillarScores.js's calcEWMA call sites (this is EWMA.js's own independent reimplementation of
  // that math — duplicated logic, not shared, flagged separately).
  const playerSessions = useMemo(() => {
    if (!playerId) return [];
    return sessions
      .flatMap(s =>
        (s.players || [])
          .filter(p => String(p.player_id) === playerId && Number(p.hmld) > 0)
          .map(p => ({
            date:    (s.session_date || '').slice(0, 10),
            title:   s.title || '',
            hmld:    Number(p.hmld),
            isMatch: s.session_type === 'match',
            isEstimated: !!p.is_estimated,
          }))
      )
      .filter(s => s.date)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sessions, playerId]);

  // >30%-imputed-chronic-window safeguard, same threshold/definition as pillarScores.js's
  // chronicImputedShare — simple count of sessions in the trailing HORIZON_CHRONIC days that are
  // estimated, not lambda-weighted. Recomputed per session so the flag tracks the same lookback
  // the ratio itself uses.
  const CHRONIC_IMPUTED_THRESHOLD = 0.3;
  function chronicImputedPct(allSessions, asOfDate) {
    const cutoffStr = addDays(asOfDate, -HORIZON_CHRONIC);
    const inWindow = allSessions.filter(s => s.date >= cutoffStr && s.date <= asOfDate);
    if (!inWindow.length) return 0;
    return inWindow.filter(s => s.isEstimated).length / inWindow.length;
  }

  // Compute Power BI EWMA for every session using full history as lookback
  const ewmaAll = useMemo(() => {
    if (!playerSessions.length) return [];
    const sessionMap = new Map(playerSessions.map(s => [s.date, s.hmld]));
    return playerSessions.map(s => {
      const acute   = calcEWMA(sessionMap, s.date, LAMBDA_ACUTE,   HORIZON_ACUTE);
      const chronic = calcEWMA(sessionMap, s.date, LAMBDA_CHRONIC, HORIZON_CHRONIC);
      const acwr    = chronic > 0 ? acute / chronic : null;
      const chronicImputed = chronicImputedPct(playerSessions, s.date);
      return { ...s, acute, chronic, acwr, chronicImputed, chronicHighlyImputed: chronicImputed > CHRONIC_IMPUTED_THRESHOLD };
    });
  }, [playerSessions]);

  // Display window — only filters chart/count, never affects EWMA lookback
  // (the season filter is applied the same way: it hides rows, it doesn't
  // truncate the history used to compute acute/chronic load).
  const displayData = useMemo(() => {
    if (!ewmaAll.length) return [];
    const cutStr = addDays(localToday(), -rangeDays);
    return ewmaAll
      .filter(d => d.date >= cutStr)
      .filter(d => {
        const season = dateToSeason(d.date);
        return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
      })
      .map(d => ({ ...d, dateLabel: fmtDate(d.date) }));
  }, [ewmaAll, rangeDays, selectedSeasons]);

  const latest = displayData[displayData.length - 1] || null;
  const zone   = riskInfo(latest?.acwr);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>EWMA · ACWR</h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Exponentially weighted moving average — acute:chronic workload ratio
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={ghostBtn}>Ind. Targets</button>
          <button style={ghostBtn}>Advanced</button>
        </div>
      </div>

      {/* ── Controls ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <select
            value={playerId}
            onChange={e => setPlayerId(e.target.value)}
            style={selectStyle}
          >
            {players.filter(p => p.active !== false).map(p => (
              <option key={p.id} value={String(p.id)}>
                {p.name || p.username || `Player ${p.id}`}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {RANGE_OPTIONS.map(d => (
            <button
              key={d}
              onClick={() => setRangeDays(d)}
              style={{
                padding: '7px 13px',
                borderRadius: 8,
                border: rangeDays === d ? '1px solid #3b82f6' : '1px solid var(--border)',
                background: rangeDays === d ? 'rgba(59,130,246,0.15)' : 'transparent',
                color: rangeDays === d ? '#93c5fd' : 'var(--text-muted)',
                fontSize: '0.82rem',
                fontWeight: rangeDays === d ? 600 : 400,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* ── Empty state ── */}
      {displayData.length === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, color: 'var(--text-muted)', textAlign: 'center', gap: 12 }}>
          <svg width="44" height="44" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2} opacity={0.4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <div>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>No GPS data found for this player.</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>Upload sessions in the Upload zone first.</p>
          </div>
        </div>
      )}

      {displayData.length > 0 && (
        <>
          {latest?.chronicHighlyImputed && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffe08a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
              <strong>{Math.round(latest.chronicImputed * 100)}% of the last {HORIZON_CHRONIC} days is estimated, not measured GPS data.</strong> ACWR below is largely reconstructed — treat it as a rough guide, not a precise ratio.
            </div>
          )}
          {/* ── Summary tiles ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'EWMA Acute',   value: latest?.acute   != null ? Math.round(latest.acute).toLocaleString()   : '—', sub: '7-day weighted',    color: null },
              { label: 'EWMA Chronic', value: latest?.chronic != null ? Math.round(latest.chronic).toLocaleString() : '—', sub: '28-day weighted',   color: null },
              { label: 'ACWR',         value: latest?.acwr    != null ? latest.acwr.toFixed(2)                       : '—', sub: zone.label,          color: zone.color },
              { label: 'Sessions',     value: displayData.length,                                                          sub: `last ${rangeDays} days`, color: null },
            ].map(({ label, value, sub, color }) => (
              <div key={label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ fontSize: '0.71rem', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: color || 'var(--text-primary)', lineHeight: 1, marginBottom: 4 }}>{value}</div>
                <div style={{ fontSize: '0.78rem', color: color || 'var(--text-muted)' }}>{sub}</div>
              </div>
            ))}
          </div>

          {/* ── ACWR line chart ── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '22px 20px 14px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>Load Ratio (ACWR)</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginLeft: 10 }}>Acute ÷ Chronic EWMA</span>
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {[
                  { color: '#94a3b8', label: 'Underload  < 0.8' },
                  { color: '#10b981', label: 'Optimal  0.8 – 1.3' },
                  { color: '#f59e0b', label: 'Caution  1.3 – 1.5' },
                  { color: '#ef4444', label: 'High Risk  > 1.5' },
                ].map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 9, height: 9, borderRadius: 2, background: color, opacity: 0.8 }} />
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={displayData} margin={{ top: 18, right: 20, left: 0, bottom: 0 }}>
                <ReferenceArea y1={0}   y2={0.8}  fill="#94a3b8" fillOpacity={0.07} />
                <ReferenceArea y1={0.8} y2={1.3}  fill="#10b981" fillOpacity={0.07} />
                <ReferenceArea y1={1.3} y2={1.5}  fill="#f59e0b" fillOpacity={0.09} />
                <ReferenceArea y1={1.5} y2={2.0}  fill="#ef4444" fillOpacity={0.09} />
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={[0, 2.0]}
                  ticks={[0, 0.5, 0.8, 1.0, 1.3, 1.5, 2.0]}
                  tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  width={38}
                />
                <Tooltip content={<ACWRTooltip />} />
                <ReferenceLine y={0.8} stroke="#94a3b8" strokeDasharray="4 3" strokeWidth={1} />
                <ReferenceLine y={1.3} stroke="#10b981" strokeDasharray="4 3" strokeWidth={1} />
                <ReferenceLine y={1.5} stroke="#f59e0b" strokeDasharray="4 3" strokeWidth={1} />
                <Line
                  type="monotone"
                  dataKey="acwr"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={<ACWRDot />}
                  activeDot={{ r: 5, fill: '#3b82f6', stroke: 'var(--bg-card)', strokeWidth: 2 }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* ── HMLD bar chart ── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '22px 20px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>HMLD per Session</span>
              <div style={{ display: 'flex', gap: 14 }}>
                {[
                  { color: '#1d4ed8', label: 'Match' },
                  { color: '#3b82f6', label: 'Training' },
                ].map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 9, height: 9, borderRadius: 2, background: color }} />
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={displayData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  width={50}
                  tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}k` : String(v)}
                />
                <Tooltip content={<HMLDTooltip />} />
                <Bar dataKey="hmld" radius={[4, 4, 0, 0]}>
                  {displayData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.isMatch ? '#1d4ed8' : '#3b82f6'} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────
const ghostBtn = {
  padding: '7px 14px',
  borderRadius: 8,
  border: '1px solid var(--border-strong)',
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const selectStyle = {
  padding: '8px 32px 8px 12px',
  borderRadius: 8,
  border: '1px solid var(--border-strong)',
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  fontSize: '0.88rem',
  fontFamily: 'inherit',
  cursor: 'pointer',
  outline: 'none',
  minWidth: 190,
  appearance: 'none',
  WebkitAppearance: 'none',
};
